// Portfolio data-quality workbench — pure checks an adviser can run before a
// review meeting to know what NOT to rely on. Operates on a normalised input
// both the client (snapshot + performance) and the report builder can assemble.
import { daysOld } from './format.js';

export type DqSeverity = 'high' | 'medium' | 'info';
export type DqArea = 'prices' | 'holdings' | 'cgt' | 'income' | 'benchmark' | 'feed' | 'coverage';

export interface DataQualityIssue {
  id: string;
  severity: DqSeverity;
  area: DqArea;
  title: string;
  detail: string;
  accountId?: string;
  securityCode?: string;
}

export interface DqHolding {
  code: string;
  name?: string | null;
  assetClass?: string | null;
  units: number;
  costBase: number;     // dollars
  currentValue: number; // dollars
}
export interface DqAccount {
  id: string;
  name: string;
  cashBalance: number;  // dollars
  holdings: DqHolding[];
  /** Feed/service-reported account value, if available, to reconcile against holdings + cash. */
  reportedValue?: number;
}

const KNOWN_CLASSES = new Set(['equity_au', 'equity_intl', 'property', 'fi', 'cash', 'alt']);

export interface DqInput {
  asOf?: string | null;
  accounts: DqAccount[];
  coverageComplete?: boolean | null;
  benchmarkConfigured?: boolean;
  pendingFeedConflicts?: number;
  staleDays?: number; // default 3
}

export function buildDataQualityIssues(input: DqInput): DataQualityIssue[] {
  const issues: DataQualityIssue[] = [];
  const staleDays = input.staleDays ?? 3;

  // Valuation freshness
  if (input.asOf !== undefined) {
    const d = daysOld(input.asOf ?? null);
    if (d == null) {
      issues.push({ id: 'val-missing', severity: 'high', area: 'prices', title: 'No valuation date', detail: 'The portfolio has no dated valuation.' });
    } else if (d > staleDays) {
      issues.push({ id: 'val-stale', severity: 'high', area: 'prices', title: 'Stale valuations', detail: `Latest valuation is ${d} days old.` });
    }
  }

  // Household coverage
  if (input.coverageComplete === false) {
    issues.push({ id: 'coverage-incomplete', severity: 'medium', area: 'coverage', title: 'Incomplete valuation coverage', detail: 'Some account valuations were carried forward to build the household series.' });
  }

  // Benchmark
  if (input.benchmarkConfigured === false) {
    issues.push({ id: 'benchmark-missing', severity: 'medium', area: 'benchmark', title: 'No benchmark configured', detail: 'Performance has no benchmark to compare against.' });
  }

  // Feed conflicts
  if ((input.pendingFeedConflicts ?? 0) > 0) {
    issues.push({ id: 'feed-conflicts', severity: 'medium', area: 'feed', title: 'Unresolved feed conflicts', detail: `${input.pendingFeedConflicts} position(s) await reconciliation.` });
  }

  // Per-holding checks (capped counts, not one row per holding)
  let missingCost = 0; let unmapped = 0; let badUnits = 0;
  const firstMissingCost: string[] = []; const firstUnmapped: string[] = [];
  for (const a of input.accounts) {
    if (a.cashBalance < 0) {
      issues.push({ id: `neg-cash-${a.id}`, severity: 'high', area: 'holdings', title: 'Negative cash balance', detail: `${a.name} has a negative cash balance.`, accountId: a.id });
    }
    if (a.reportedValue != null) {
      const calc = a.cashBalance + a.holdings.reduce((sum, h) => sum + h.currentValue, 0);
      if (Math.abs(calc - a.reportedValue) > Math.max(1, Math.abs(a.reportedValue) * 0.001)) {
        issues.push({ id: `value-mismatch-${a.id}`, severity: 'medium', area: 'holdings', accountId: a.id,
          title: 'Account value mismatch',
          detail: `${a.name}: holdings + cash (${Math.round(calc)}) differs from the reported value (${Math.round(a.reportedValue)}).` });
      }
    }
    for (const h of a.holdings) {
      if (h.currentValue > 0 && h.costBase <= 0) { missingCost += 1; if (firstMissingCost.length < 3) firstMissingCost.push(h.code); }
      if (!h.assetClass || !KNOWN_CLASSES.has(h.assetClass)) { unmapped += 1; if (firstUnmapped.length < 3) firstUnmapped.push(h.code); }
      if (h.units <= 0 && h.currentValue > 0) { badUnits += 1; }
    }
  }
  if (missingCost > 0) issues.push({ id: 'missing-costbase', severity: 'medium', area: 'cgt', title: 'Missing cost base', detail: `${missingCost} holding(s) have a value but no cost base (e.g. ${firstMissingCost.join(', ')}). CGT figures may be understated.` });
  if (unmapped > 0) issues.push({ id: 'unmapped-class', severity: 'info', area: 'holdings', title: 'Unclassified asset class', detail: `${unmapped} holding(s) have no recognised asset class (e.g. ${firstUnmapped.join(', ')}). Allocation may be incomplete.` });
  if (badUnits > 0) issues.push({ id: 'bad-units', severity: 'high', area: 'holdings', title: 'Invalid units', detail: `${badUnits} holding(s) have a value but zero/negative units.` });

  return issues;
}

export function dqCounts(issues: DataQualityIssue[]): { high: number; medium: number; info: number; blocking: number } {
  const high = issues.filter((i) => i.severity === 'high').length;
  const medium = issues.filter((i) => i.severity === 'medium').length;
  const info = issues.filter((i) => i.severity === 'info').length;
  return { high, medium, info, blocking: high };
}
